import React from "react";
import { StudyStrip, StudyNotes, SonmiMark, Presence } from "./StudyChrome";

/**
 * Visual study 2 — People Vessel, entry state.
 *
 * Not a directory. Not a grid. Opens on the steward's current
 * *understanding of the team*. Individual people appear as
 * quiet presences, not filterable rows.
 *
 * Functional controls (search, add) are quietly available at the
 * base of the vessel, honoring the functional-permission
 * distinction — they exist without commanding the composition.
 */

const TEAM = [
  { initials: "SC", name: "Simone Cortez",  note: "a week into her new schedule" },
  { initials: "DM", name: "Devon Marsh",    note: "returns tomorrow" },
  { initials: "AB", name: "Amira Bell" },
  { initials: "TF", name: "Tanya Fielding" },
  { initials: "PA", name: "Priya Anand" },
  { initials: "JW", name: "Jules Whitaker" },
  { initials: "NR", name: "Noor Rahim" },
  { initials: "ME", name: "Maren Ellis",    note: "quietly steady" },
];

export default function PeopleVessel() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-stone-700">
      <StudyStrip current="/sonmi/study/people-vessel" />

      {/* Steward + ambient SONMI, same as Shell — establishes continuity. */}
      <header className="max-w-5xl mx-auto px-8 pt-16 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="steward-presence">
          <div className="w-9 h-9 rounded-full bg-stone-100 border border-stone-100 flex items-center justify-center text-stone-500"
               style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 14 }}>
            D
          </div>
          <div className="text-[13px] text-stone-500">Damon</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-stone-400" data-testid="sonmi-ambient">
          <SonmiMark />
          <span>here, quietly</span>
        </div>
      </header>

      {/* Vessel title — Cormorant, light, unhurried. No filter chips, no tabs,
          no "add teammate" pill at the top. */}
      <section className="max-w-3xl mx-auto px-8 pt-20 text-center" data-testid="vessel-title">
        <div
          className="text-stone-900"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "2rem", letterSpacing: "-0.005em" }}
        >
          Plantation
        </div>
        <div className="mt-4 text-[13px] uppercase tracking-[0.18em] text-stone-400">
          your team, right now
        </div>
      </section>

      {/* The current understanding — a single passage, not a list of metrics.
          Boutique color is deliberately absent here — belonging is not what this
          moment is about. */}
      <section className="max-w-2xl mx-auto px-8 pt-14" data-testid="team-understanding">
        <p className="text-[15px] text-stone-600 font-light leading-relaxed">
          Simone is a week into her new schedule. Devon comes back from time off
          tomorrow. Amira has been quietly steady. Priya has been leaving a
          little later on Thursdays. The rest is settled.
        </p>
      </section>

      {/* The team — a loose composition, not a grid. Two people carry a small,
          Phase-1 line of noticing beneath their name; six do not. The people are
          people, not rows. */}
      <section
        data-testid="team-composition"
        className="max-w-4xl mx-auto px-8 pt-24 pb-8 flex flex-wrap justify-center gap-x-12 gap-y-16"
      >
        {TEAM.map((p) => (
          <Presence key={p.name} initials={p.initials} name={p.name} note={p.note} />
        ))}
      </section>

      {/* Functional access, quietly available. Not a primary action bar.
          These honor the functional-permission distinction: the steward's hand
          does not need to earn its way to a control. */}
      <section className="max-w-3xl mx-auto px-8 pt-16 flex items-center justify-center gap-8 text-[12px] text-stone-400"
               data-testid="functional-quiet">
        <button type="button" className="hover:text-stone-700 transition-colors" data-testid="functional-find">
          find someone
        </button>
        <span className="text-stone-200">·</span>
        <button type="button" className="hover:text-stone-700 transition-colors" data-testid="functional-add">
          add a new teammate
        </button>
      </section>

      <StudyNotes
        testing="Whether a People Vessel can open on *understanding of the team* rather than on a filterable list — and whether individuals can appear as quiet presences without being reduced to cards, filters, or alerts."
        understandFirst="Where the team stands right now, in one honest sentence. That most of it is settled. That two people are in transitions and one is showing a small pattern (Priya on Thursdays) SONMI has noticed but has not yet interpreted."
        withheld="Filter chips (role, boutique, active/inactive), the tabs (Directory · Modality · Hiring · Journey), the alerts strip with severity dots, the 'Add teammate' primary pill, the HR-file drawer icon per person, and any per-person status badge. All of these existed in People.jsx; none appears here."
        throughAttention="Each Presence is enterable. Attending to Simone leads to the Expanded state (study 3), where the vessel reorganizes around one person rather than routing to a new page. 'Find someone' and 'add a new teammate' remain available for functional need without demanding attention."
        principles="P26 (restraint before observation — only two Phase-1 lines appear, both very small; the other six people carry no SONMI-authored note) · P28 (steward's lived experience — the opening paragraph reads the way a manager would describe their team to a trusted colleague, not the way a database would summarize its rows) · Situational-tool vs. organizing-metaphor (a filter bar could still appear later inside this vessel — but not as the opening frame)."
      />
    </div>
  );
}
